import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { UserFragment, UserRoleTypes, UsersByKeywordQueryVariables, UserStatus, useUsersByKeywordQuery } from "../../graphql"
import { UsersList } from "./UsersList"

export type UsersByKeywordProps = {
	keyword: string
	typeId?: string
	roleType?: UserRoleTypes
	status?: UserStatus
	since?: Date
	until?: Date
	onUserClick?: (user: UserFragment) => void
}

export const UsersByKeyword: React.FC<UsersByKeywordProps> = ({ keyword, typeId, roleType, status, since, until, onUserClick }) => {
	const [filter, setFilter] = useState({ typeId, roleType, keyword, status, since, until })

	const [pagination, setPagination] = useState<UsersByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useUsersByKeywordQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword, typeId, roleType, status, since, until })
			setPagination((prev) => ({
				...prev,
				page: 1,
			}))
		}, 400)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword, typeId, roleType, status, since, until])

	const nextPage = useCallback(() => {
		if (data?.usersByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data])



	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.usersByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading users</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.usersByKeyword.users && data.usersByKeyword.users.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.usersByKeyword.users.length || 0} user
						{(data.usersByKeyword.users.length || 0) > 1 ? "s" : ""}
					</Text>

					<UsersList users={data.usersByKeyword.users} loadMore={nextPage} onUserClick={onUserClick} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more users</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any users.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
